import React, { useEffect, useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import Button, { ButtonThemes } from "ui-components/button/button";
import Table from "ui-components/table/table";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledDateRangeInput from "ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import { CarrierAssignmentChangeLogItem } from "app/data/carrier-assignment-changes/models";
import { useCarrierAssignmentChangeLogSearch } from "features/static-load/hooks/useCarrierAssignmentChangeLog";
import "./loadBoardCarrierChangeLog.scss";

type LoadBoardCarrierChangeLogProps = {
  show: boolean;
  onClose: () => void;
};

type LoadBoardCarrierChangeLogRow = {
  changedAt: string;
  modifiedBy: string;
  previousCarrier: string;
  newCarrier: string;
  sourceId: string;
  reason: string;
};

const formatCarrierLabel = (
  name?: string | null,
  number?: string | number | null
): string => {
  if (name && number != null && number !== "") return `${name} (${number})`;
  if (name) return name;
  if (number != null && number !== "") return String(number);
  return "—";
};

const columnHelper = createColumnHelper<LoadBoardCarrierChangeLogRow>();

const columns = [
  columnHelper.accessor("changedAt", {
    id: "changedAt",
    header: "Timestamp",
    cell: (cellProps) => {
      const value = cellProps.getValue();
      return (
        <>
          {value && (
            <div className="xgs-loadboard-carrier-change-log__date">
              <div>{(value as any).toUiDateFormat?.() ?? value}</div>
              <div className="xgs-loadboard-carrier-change-log__gray-value">
                {(value as any).toUiTimeShortFormat?.() ?? ""}
              </div>
            </div>
          )}
        </>
      );
    },
    size: 140,
    minSize: 120,
  }),
  columnHelper.accessor("modifiedBy", {
    id: "modifiedBy",
    header: "User",
    size: 120,
    minSize: 80,
  }),
  columnHelper.accessor("previousCarrier", {
    id: "previousCarrier",
    header: "Previous Carrier",
    size: 180,
    minSize: 120,
  }),
  columnHelper.accessor("newCarrier", {
    id: "newCarrier",
    header: "New Carrier",
    size: 180,
    minSize: 120,
  }),
  columnHelper.accessor("sourceId", {
    id: "sourceId",
    header: "Manifest",
    size: 140,
    minSize: 100,
  }),
  columnHelper.accessor("reason", {
    id: "reason",
    header: "Reason",
    size: 180,
    minSize: 120,
  }),
];

const mapToRows = (items: CarrierAssignmentChangeLogItem[]): LoadBoardCarrierChangeLogRow[] =>
  items.map((item) => ({
    changedAt: item.changedAt,
    modifiedBy: item.modifiedBy,
    previousCarrier: formatCarrierLabel(item.previousCarrierName, item.previousCarrierNumber),
    newCarrier: formatCarrierLabel(item.newCarrierName, item.newCarrierNumber),
    sourceId: item.sourceId || "—",
    reason: item.reasonDescription || item.reasonCode || "—",
  }));

/**
 * Load Board carrier assignment history.
 * GET /carrier-assignment-changes?sourceType=LOAD_BOARD
 */
const LoadBoardCarrierChangeLog: React.FC<LoadBoardCarrierChangeLogProps> = ({
  show,
  onClose,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFrom, setDateFrom] = useState<string | undefined>();
  const [dateTo, setDateTo] = useState<string | undefined>();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const toApiDate = (value?: string) =>
    value ? (value as any).toApiDateFormat?.() ?? value : undefined;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
  } = useCarrierAssignmentChangeLogSearch(
    {
      sourceType: "LOAD_BOARD",
      search: debouncedSearch || undefined,
      dateFrom: toApiDate(dateFrom),
      dateTo: toApiDate(dateTo),
    },
    show
  );

  const rows = useMemo(() => {
    const items = data?.pages.flatMap((page) => page.content) ?? [];
    return mapToRows(items);
  }, [data]);

  const onSearch = () => {
    setDebouncedSearch(searchInput.trim());
  };

  const onClearFilters = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  return (
    <SlideOutSidebar
      header="Carrier Change Log"
      onClose={onClose}
      show={show}
      size="medium"
      className="xgs-loadboard-carrier-change-log"
    >
      <div className="xgs-loadboard-carrier-change-log__filters">
        <LabeledTextInput
          label="Search"
          labelMode={LabelModes.column}
          className="xgs-loadboard-carrier-change-log__filter-input"
          value={searchInput}
          onChange={(e) => setSearchInput(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch()}
          type="text"
          placeholder="Door, manifest, user, carrier..."
        />
        <LabeledDateRangeInput
          label="Date Range:"
          labelMode={LabelModes.column}
          className="xgs-loadboard-carrier-change-log__filter-dates"
          start={dateFrom}
          end={dateTo}
          maxDate={new Date()}
          onStartChange={(v) => setDateFrom(v || undefined)}
          onEndChange={(v) => setDateTo(v || undefined)}
        />
        <Button
          className="xgs-loadboard-carrier-change-log__filter-button"
          theme={ButtonThemes.gray}
          onClick={onClearFilters}
          disabled={!searchInput && !dateFrom && !dateTo}
        >
          Clear Filters
        </Button>
        <Button
          className="xgs-loadboard-carrier-change-log__filter-button"
          theme={ButtonThemes.blue}
          onClick={onSearch}
        >
          Search
        </Button>
      </div>

      <div className="xgs-loadboard-carrier-change-log__table-wrapper">
        {!isError && (
          <Table
            isLoading={isLoading}
            columns={columns}
            data={rows}
            rowHeight={48}
            minTableHeight={200}
            infiniteScroll={true}
            noResultsText="No Load Board carrier changes found."
            infiniteScrollLoading={isFetchingNextPage}
            infiniteScrollHasNext={!!hasNextPage}
            onInfiniteScroll={() => {
              if (hasNextPage) fetchNextPage();
            }}
          />
        )}
      </div>

      {isError && (
        <XGSErrorMessage className="xgs-loadboard-carrier-change-log__error">
          Error while obtaining Load Board carrier change log
        </XGSErrorMessage>
      )}
    </SlideOutSidebar>
  );
};

export default LoadBoardCarrierChangeLog;
